
import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { vxm } from '@/store'
import {
  Commit,
  CommitTaskSource,
  Dimension,
  DimensionDifference,
  RunWithDifferences,
  TarTaskSource
} from '@/store/types'
import NotFound404 from './NotFound404.vue'
import RunDetail from '@/components/rundetail/RunDetail.vue'
import CommitDetail from '@/components/rundetail/CommitDetail.vue'
import RunTimeline from '@/components/rundetail/RunTimeline.vue'
import { NotFoundError } from '@/store/modules/commitDetailComparisonStore'
import TarOverview from '@/components/overviews/TarOverview.vue'
import RunSignificanceChips from '@/components/runs/RunSignificanceChips.vue'

@Component({
  components: {
    'run-significance-chips': RunSignificanceChips,
    'tar-overview': TarOverview,
    'page-404': NotFound404,
    'run-detail': RunDetail,
    'commit-detail': CommitDetail,
    'run-timeline': RunTimeline
  }
})
export default class RunCommitDetailView extends Vue {
  private runWithDifferences: RunWithDifferences | null = null
  private commit: Commit | null = null
  private tarSource: TarTaskSource | null = null
  private show404: boolean = false
  private finishedLoading: boolean = false

  private get firstComponent(): string {
    return this.$route.params['first']
  }

  private get secondComponent(): string | undefined {
    return this.$route.params['second']
  }

  private get dimensionDifferences(): DimensionDifference[] | undefined {
    if (!this.runWithDifferences) {
      return undefined
    }
    return this.runWithDifferences.significantDifferences
  }

  private get significantFailedDimensions(): Dimension[] {
    if (!this.runWithDifferences) {
      return []
    }
    return this.runWithDifferences.significantFailedDimensions
  }

  private async navigateToDetailGraph(dimension: Dimension) {
    if (!this.commit) {
      return
    }
    const commit = this.commit

    vxm.detailGraphModule.centerOnCommit({ commit, dimension })

    // Find the datapoint so we can set it as reference
    const allDataPoints = await vxm.detailGraphModule.fetchDetailGraph()
    const detailPoint = allDataPoints.find(it => it.hash === commit.hash)

    if (detailPoint) {
      vxm.detailGraphModule.referenceDatapoint = {
        datapoint: detailPoint,
        seriesId: dimension.toString()
      }
    }

    // And finally navigate away
    await this.$router.push({
      name: 'repo-detail',
      params: { id: this.commit.repoId }
    })
  }

  @Watch('firstComponent')
  @Watch('secondComponent')
  private async fetchCommitAndRun() {
    try {
      await this.fetchCommitAndRunImpl()
    } catch (e) {
      if (e instanceof NotFoundError) {
        this.show404 = true
        return
      }
      throw e
    }
  }

  private async fetchCommitAndRunImpl() {
    this.show404 = false
    this.runWithDifferences = null
    this.commit = null
    this.tarSource = null
    this.finishedLoading = false

    if (this.firstComponent && !this.secondComponent) {
      // we have a run id
      this.runWithDifferences = await vxm.commitDetailComparisonModule.fetchRun(
        this.firstComponent
      )
      const run = this.runWithDifferences.run

      if (run.source instanceof CommitTaskSource) {
        this.commit = await vxm.commitDetailComparisonModule.fetchCommit({
          repoId: run.source.commitDescription.repoId,
          commitHash: run.source.commitDescription.hash
        })
      } else {
        this.tarSource = run.source
      }
    } else if (this.firstComponent && this.secondComponent) {
      // we have a repo id and commit hash
      this.commit = await vxm.commitDetailComparisonModule.fetchCommit({
        repoId: this.firstComponent,
        commitHash: this.secondComponent
      })
      if (this.commit.runs.length > 0) {
        this.runWithDifferences =
          await vxm.commitDetailComparisonModule.fetchRun(
            this.commit.runs[0].runId
          )
      }
    } else {
      // We got weird things
      this.show404 = true
    }

    this.finishedLoading = true
  }

  mounted(): void {
    this.fetchCommitAndRun()
  }
}
